import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { MoreHorizontal, Share, Archive, Trash2, Pencil, ChevronDown, List } from "lucide-react"
import { cn } from "@/lib/utils"
import { ROUTES } from "@/routes/path"
import { useListThreads } from "@/hooks/useChat"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { DeleteThreadDialog } from "./delete-thread"
import { ArchiveThreadDialog } from "./archive-thread"
import { EditThreadInput } from "./edit-thread"
import { Thread } from "@/types/api"

export function RecentThreads() {
  const location = useLocation()

  const currentThreadId = location.pathname.replace(ROUTES.DASHBOARD.STUDIO.INDEX + "/", "")

  const { data: threads, isPending: isThreadsPending } = useListThreads()
  const recentThreads = threads?.slice(0, 15) || []
  const hasMoreThreads = threads && threads.length > 15
  const navigate = useNavigate()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [threadToDelete, setThreadToDelete] = useState<Thread | null>(null)
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
  const [threadToArchive, setThreadToArchive] = useState<Thread | null>(null)
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState("")
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Truncate thread name if too long
  const truncateName = (name: string, maxLength: number = 30) => {
    if (name.length <= maxLength) return name
    return name.substring(0, maxLength) + "..."
  }

  const handleThreadSelect = (threadId: string) => {
    navigate(ROUTES.DASHBOARD.STUDIO.THREAD(threadId))
  }

  const handleThreadShare = (threadId: string) => {
    console.log("Share thread:", threadId)
  }

  const handleThreadRename = (threadId: string) => {
    const thread = threads?.find((t) => t.id === threadId)
    if (thread) {
      setEditingThreadId(threadId)
      setEditingName(thread.name)
    }
  }

  const handleEditEnd = () => {
    setEditingThreadId(null)
    setEditingName("")
  }

  const handleThreadArchive = (threadId: string) => {
    const thread = threads?.find((t) => t.id === threadId)
    if (thread) {
      setThreadToArchive(thread)
      setArchiveDialogOpen(true)
    }
  }

  const handleThreadDelete = (threadId: string) => {
    const thread = threads?.find((t) => t.id === threadId)
    if (thread) {
      setThreadToDelete(thread)
      setDeleteDialogOpen(true)
    }
  }

  return (
    <div>
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="mb-1 flex w-full items-center gap-1 px-3 text-xs text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
      >
        <span>Recents</span>
        <ChevronDown className={cn("h-3 w-3 transition-transform", isCollapsed && "rotate-[-90deg]")} />
      </button>
      {!isCollapsed && (
        <div className="space-y-0.5">
          {isThreadsPending ? (
            <div className="space-y-1.5 px-3 py-1">
              {["w-[94%]", "w-[80%]", "w-[88%]", "w-[72%]", "w-[90%]", "w-[76%]"].map(
                (widthClass, i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-6 max-w-full animate-pulse rounded-md bg-gray-100 dark:bg-[#1F1F23]",
                      widthClass,
                    )}
                  />
                ),
              )}
            </div>
          ) : recentThreads.length > 0 ? (
            <>
              {recentThreads.map((thread) => (
                <div key={thread.id} className="group relative">
                  {editingThreadId === thread.id ? (
                    <div className="px-3 py-2">
                      <EditThreadInput
                        threadId={thread.id}
                        currentName={thread.name}
                        isEditing={true}
                        onEditStart={() => { }}
                        onEditEnd={handleEditEnd}
                        onNameChange={setEditingName}
                        editingName={editingName}
                      />
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleThreadSelect(thread.id)}
                        className={cn(
                          "w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors",
                          currentThreadId === thread.id
                            ? "bg-gray-100 text-gray-900 dark:bg-[#1F1F23] dark:text-white"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-[#1F1F23] dark:hover:text-white"
                        )}
                        title={thread.name}
                      >
                        <span className="block truncate">{truncateName(thread.name)}</span>
                      </button>

                      {/* Options dropdown - appears on hover */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 opacity-0 transition-opacity hover:bg-gray-50 group-hover:opacity-100 dark:hover:bg-gray-700"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4 text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onClick={() => handleThreadShare(thread.id)}>
                            <Share className="mr-2 h-4 w-4" />
                            Share
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleThreadRename(thread.id)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleThreadArchive(thread.id)}>
                            <Archive className="mr-2 h-4 w-4" />
                            Archive
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleThreadDelete(thread.id)}
                            className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </div>
              ))}
              {hasMoreThreads && (
                <button
                  onClick={() => navigate(ROUTES.DASHBOARD.STUDIO.LIST)}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-[#1F1F23] dark:hover:text-white"
                >
                  <List className="mr-1 h-4 w-4 text-gray-500" />
                  <span>All chats</span>
                </button>
              )}
            </>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              No recent chats
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <DeleteThreadDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        threadToDelete={threadToDelete}
        currentThreadId={currentThreadId}
      />

      {/* Archive confirmation dialog */}
      <ArchiveThreadDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        threadToArchive={threadToArchive}
        currentThreadId={currentThreadId}
      />
    </div>
  )
}
